import { useCallback, useEffect, useMemo, useState } from 'react'
import type { MetaData } from '@/components/tables'
import { useParentTicketStore } from '@/store/parentTicket'

export type TicketListView = 'all' | 'my' | 'my_team' | 'unassigned' | 'trash'

export type UseParentTicketsOptions = {
  view?: TicketListView
  /** For "my" view: match agentName against these (e.g. user_login, full name) */
  userIdentifiers?: string[]
}

export function useParentTickets(options: UseParentTicketsOptions = {}) {
  const { view = 'all', userIdentifiers = [] } = options
  const tickets = useParentTicketStore((s) => s.tickets)

  const [page, setPage] = useState(1)
  const perPage = 10

  const filteredTickets = useMemo(() => {
    if (view === 'all') return tickets
    if (view === 'unassigned') return tickets.filter((t) => !t.agentName?.trim())
    if (view === 'my_team') return tickets
    if (view === 'my' && userIdentifiers.length) {
      const set = new Set(userIdentifiers.map((s) => s?.toLowerCase().trim()).filter(Boolean))
      return tickets.filter((t) => t.agentName && set.has(t.agentName.toLowerCase().trim()))
    }
    return tickets
  }, [tickets, view, userIdentifiers])

  useEffect(() => {
    setPage(1)
  }, [view])

  const meta = useMemo<MetaData>(() => {
    const total = filteredTickets.length
    const lastPage = Math.max(1, Math.ceil(total / perPage))

    return {
      current_page: page,
      last_page: lastPage,
      per_page: perPage,
      total,
      from: total === 0 ? 0 : (page - 1) * perPage + 1,
      to: Math.min(page * perPage, total),
      links: [
        { url: page > 1 ? `?page=${page - 1}` : null, label: 'prev', active: false },
        ...Array.from({ length: lastPage }, (_, i) => {
          const p = i + 1
          return { url: `?page=${p}`, label: `${p}`, active: p === page }
        }),
        { url: page < lastPage ? `?page=${page + 1}` : null, label: 'next', active: false },
      ],
    }
  }, [filteredTickets.length, page])

  const setPageFromUrl = useCallback((url: string | null) => {
    if (!url) return
    const qs = url.includes('?') ? url.split('?')[1] : url
    const p = Number(new URLSearchParams(qs).get('page') ?? '1')
    setPage(Number.isFinite(p) && p > 0 ? p : 1)
  }, [])

  const pagedTickets = useMemo(() => {
    const start = (page - 1) * perPage
    return filteredTickets.slice(start, start + perPage)
  }, [filteredTickets, page])

  return {
    tickets: pagedTickets,
    totalCount: filteredTickets.length,
    meta,
    setPageFromUrl,
  }
}
